import {
  AppBar,
  Badge,
  Box,
  ButtonBase,
  IconButton,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import { useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import LockScreen from "../../pages/lock-screen/LockScreen";
import { bgBlur } from "../../utils/cssStyles";
import Iconify from "../iconify/Iconify";
import NewOrderDialog from "../new-order-dialog/NewOrderDialog";
import NotificationDrawer from "../notification-drawer/NotificationDrawer";
import AccountPopover from "./AccountPopover";
import "./Header.css";
//import LanguagePopover from './LanguagePopover';
//import NotificationsPopover from './NotificationsPopover';

const NAV_WIDTH = 90;

const HEADER_MOBILE = 64;

const HEADER_DESKTOP = 65;

const StyledRoot = styled(AppBar)(({ open, theme }) => ({
  ...bgBlur({ color: theme.palette.background.default }),
  boxShadow: "none",
  border: "1px solid rgba(145, 158, 171, 0.12)",
  background: "#fff",
  [theme.breakpoints.up("lg")]: {
    width: `calc(100% - ${open ? NAV_WIDTH : 0}px)`,
  },
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  minHeight: HEADER_MOBILE,
  [theme.breakpoints.up("lg")]: {
    minHeight: HEADER_DESKTOP,
    padding: theme.spacing(0, 2),
  },
}));

Header.propTypes = {
  openNav: PropTypes.bool,
  onOpenNav: PropTypes.func,
};

export default function Header({ openNav, onOpenNav }) {
  const { details } = useSelector((state) => state.store);
  const { activeCount } = useSelector((state) => state.table);
  const { activeKotCount } = useSelector((state) => state.table);

  const navigate = useNavigate();
  const [isNewOrderDialogOpen, setIsNewOrderDialogOpen] = useState(false);
  const [locked, setLocked] = useState(false);
  const [pinSuccess, setPinSuccess] = useState(false);
  const [mode, setMode] = useState("unlock");
  const location = useLocation();
  const isTables = location.pathname.includes("tables");
  const isOrders = location.pathname.includes("orders");

  const goToTableView = async () => {
    navigate(`/app/tables`);
  };

  const goToOrders = async () => {
    navigate(`/app/orders`);
  };

  const handleDialogCancel = () => {
    setIsNewOrderDialogOpen(false);
  };

  const handleNewOrderDialog = () => {
    setIsNewOrderDialogOpen(true);
  };

  const handleLock = () => {
    setMode("unlock");
    setLocked(true);
  };

  const handlePinChanges = () => {
    setMode("create");
    setPinSuccess(true);
  };

  const handleKOTViewClick = () => {
    navigate(`/app/kot-view`);
  };

  return (
    <>
      <StyledRoot open={openNav}>
        <StyledToolbar>
          <IconButton
            className="header-open-ico"
            onClick={onOpenNav}
            sx={{
              mr: 1,
              color: "text.primary",
            }}
          >
            <Iconify
              icon={
                openNav
                  ? "material-symbols:chevron-left"
                  : "material-symbols:chevron-right"
              }
            />
          </IconButton>
          <Box
            className="header-store-name"
            onClick={() => navigate("/app/home")}
            sx={{
              cursor: "pointer",
              transition: "all 0.3s ease",
              "&:hover": {
                opacity: 0.9,
              },
            }}
          >
            {details?.store_name}
          </Box>
          <Box
            style={{
              marginLeft: 15,
            }}
          >
            <Box className="header-menu">
              <ButtonBase
                // style={{ color: "#FFF", backgroundColor: "#3f51b5" }}
                className="header-menu-item"
                onClick={handleNewOrderDialog}
              >
                Take New Order
              </ButtonBase>
              <ButtonBase
                className={`header-menu-item ${isTables ? "active" : ""}`}
                onClick={goToTableView}
              >
                <Box>Table View</Box>
                <Box
                  className="table-badge"
                  sx={{
                    background: activeCount === 0 ? "#424242" : "#00ab55",
                  }}
                >
                  {activeCount}
                </Box>
              </ButtonBase>
              <ButtonBase
                className={`header-menu-item ${isOrders ? "active" : ""}`}
                onClick={goToOrders}
              >
                <Box>Orders</Box>
              </ButtonBase>
            </Box>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            gap="10px"
            spacing={{
              xs: 0.5,
              sm: 1,
            }}
          >
            <Tooltip title="TPOS Support">
              <Box
                // onClick={handleSupportClick}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.2,
                  py: 0.6,
                  borderRadius: 1,
                  cursor: "pointer",
                  color: "#000",
                  border: "1px solid #f8f8f8",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.04)",
                  },
                }}
              >
                {/* Icon */}
                <Iconify icon="mdi:headset" width={20} />

                {/* Text */}
                <Box sx={{ lineHeight: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, lineHeight: 1.1 }}
                  >
                    <Box
                      component="span"
                      sx={{
                        color: "#f6521a",
                        display: "inline-block",
                        transform: "rotate(-10deg)",
                        mr: "2px",
                      }}
                    >
                      T
                    </Box>
                    POS
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    Support
                  </Typography>
                </Box>
              </Box>
            </Tooltip>

            <Tooltip title="Watch Tutorial">
              <IconButton
                // onClick={handleKOTViewClick}
                sx={{ p: 1.2, position: "relative" }}
              >
                <Iconify icon="octicon:play-16" />
              </IconButton>
            </Tooltip>

            <Tooltip title="View KOT">
              <IconButton
                onClick={handleKOTViewClick}
                sx={{ p: 1.2, position: "relative" }}
              >
                <Badge
                  badgeContent={activeKotCount}
                  sx={{
                    "& .MuiBadge-badge": {
                      backgroundColor: "#424242",
                      color: "white",
                    },
                  }}
                >
                  <Iconify icon="lucide:cooking-pot" />
                </Badge>
              </IconButton>
            </Tooltip>
            <NotificationDrawer />
            {/* <LanguagePopover />
                    <NotificationsPopover /> */}
            <AccountPopover
              onLock={handleLock}
              onPinChanges={handlePinChanges}
            />
          </Stack>
        </StyledToolbar>
      </StyledRoot>

      {isNewOrderDialogOpen && (
        <NewOrderDialog
          isNewOrderDialogOpen={isNewOrderDialogOpen}
          onDialogCancel={handleDialogCancel}
        />
      )}

      {(locked || pinSuccess) && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            bgcolor: "rgba(0,0,0,0.85)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2000,
          }}
        >
          <LockScreen
            mode={mode}
            onUnlock={() => setLocked(false)}
            onClose={() => setPinSuccess(false)}
          />
        </Box>
      )}
    </>
  );
}
